import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { FlexColumn, FlexRow } from 'design-system';
import { selectLearningBrandLogo } from '../../../redux/learning/selectors';
import CompactProgressStepper from '../../components/CompactProgressStepper';
import LearningShell from '../../components/LearningShell';
import ProgressSidebar from '../../components/ProgressSidebar';
import WarningBanner from '../../components/WarningBanner';
import { useGuidanceFlow } from '../../hooks/useGuidanceFlow';
import DocumentStep from './DocumentStep';
import VideoStep from './VideoStep';

export default function GuidanceStep({ course, onCompleteGuidance }) {
  const flow = useGuidanceFlow({ course, onCompleteGuidance });
  const { currentGuidance } = flow;
  const brandLogo = useSelector(selectLearningBrandLogo);

  return (
    <LearningShell
      layout="guidance"
      showWarning={false}
    >
      <FlexRow align="start" fullWidth gap="var(--ds-space-9)" className="ld-guidance-shell">
        <CompactProgressStepper steps={flow.sidebarSteps} />
        <FlexColumn align="stretch" fullWidth={false} className="ld-guidance-shell__sidebar">
          <ProgressSidebar steps={flow.sidebarSteps} />
        </FlexColumn>
        <FlexColumn align="stretch" fullWidth gap="var(--ds-space-9)" className="ld-guidance-shell__main">
          {currentGuidance?.type === 'video' ? (
            <VideoStep
              guidance={currentGuidance}
              logoSrc={brandLogo}
              videoPlayer={flow.videoPlayer}
              onContinue={flow.continueGuidance}
              canContinue={flow.canContinue}
            />
          ) : null}
          {/* `key` is required: consecutive document steps would otherwise
              reuse the mounted reader and open still maximized. */}
          {currentGuidance?.type === 'document' ? (
            <DocumentStep
              key={currentGuidance.id}
              guidance={currentGuidance}
              readPercent={flow.documentReadPercent}
              documentComplete={flow.documentComplete}
              documentNeedsScroll={flow.documentNeedsScroll}
              onScroll={flow.handleDocumentScroll}
              onMeasureScroll={flow.measureDocumentScroll}
              onContinue={flow.continueGuidance}
              canContinue={flow.canContinue}
            />
          ) : null}
          <WarningBanner message={course.progressWarning} />
        </FlexColumn>
      </FlexRow>
    </LearningShell>
  );
}

GuidanceStep.propTypes = {
  course: PropTypes.shape({
    description: PropTypes.string,
    logoSrc: PropTypes.string,
    progressWarning: PropTypes.string,
    guidanceSteps: PropTypes.arrayOf(PropTypes.object),
    progressSteps: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  onCompleteGuidance: PropTypes.func,
};
